// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// palette.ts — every colour that carries meaning, in one place so it can be MEASURED.
//
// No renderer may contain a colour literal. That rule is not tidiness: a sibling game shipped walls
// drawn at 1.14:1 against their own board — in the right cells, at the right size, and invisible —
// and the screenshot looked atmospheric, because on a deliberately dark palette "invisible" reads as
// mood. tests/contrast.test.ts holds every mark here to >= 3:1 (WCAG 2.1 SC 1.4.11, the floor for a
// non-text graphic) against EVERY surface it is really drawn on, in every state that surface can be
// in — and the compartment surface changes underneath everything as it floods.
//
// FIRE AND FLOOD ARE SEPARATED BY POSITION AND SHAPE, NEVER BY BRIGHTNESS. They sit at close
// luminance on purpose, because amber-vs-cyan is the Okabe-Ito axis that survives all three
// dichromacies; what tells them apart for everyone is that water RISES from the bottom with a flat
// waterline and fire FALLS from the top as flickering triangles.

export const PALETTE = {
  /** The space around the ship. */
  hull: '#0D1420',
  /** A compartment at rest — the surface most marks are drawn on. */
  cell: '#1B2A3D',
  /** HUD and panel backing. */
  panel: '#121C29',
  /**
   * The selected compartment. This was #26405C and failed at 2.83:1 against the unpowered tail —
   * the classic "the selected tile is a fourth surface nobody measured" trap. Darkened on paper,
   * before a line of the renderer existed, to #1D3450 — and darkened AGAIN, to here, when the
   * wire-vs-dark assertion arrived (below).
   */
  sel: '#1B304A',
  /**
   * The powered spine. Solid fill; the tail is hollow and dashed.
   *
   * THIS TRIO IS SOLVED, NOT PICKED. `wire` and `dark` are the two halves of one readout, so they
   * have to clear 3:1 against EACH OTHER as well as against every surface — and `dark` also has to
   * clear 3:1 against `sel`, the lightest surface, which squeezes it from below. At the design's
   * #FFD24A / #6B86A3 / #1D3450 the cable's two halves measured 2.62:1 and NO value of `dark`
   * existed that satisfied both ends (the ceiling for wire/dark sat at luminance 0.1928, the floor
   * for dark/sel at 0.1989). All three moved: the amber lightened, the tail darkened, the selection
   * darkened. wire/dark and dark/sel now sit at 3.14:1 apiece, which is the widest either can be
   * without pushing the amber out of the amber family.
   */
  wire: '#FFDA66',
  /** The cable beyond the budget. Carries a hull-coloured halo so it survives the flood fill. */
  dark: '#637D99',
  flood: '#3AA8D8',
  fire: '#FF9142',
  breach: '#E8477F',
  /** Hull, transit, a sealed compartment. Never co-located with `fire`. */
  good: '#4FD39A',
  /**
   * A destroyed compartment. Carries an X hatch as well.
   *
   * A destroyed compartment still takes water — `hostStep` skips destroyed cells for fire but not
   * for flood — so the hatch is drawn on the flood fill, where #9AA4AD measured 2.37:1. Lightened
   * to #B6BEC6, and lightened AGAIN to here when raising FLOOD_ALPHA to 0.7 made the water lighter
   * and put the hatch back under the floor at 2.28:1. It has to be pale: there is no colour that is
   * darker than the water and still clears the dry compartment, so the only direction available is
   * up. 3.18:1 on the fill, 10.8:1 on a dry compartment. The scar is permanent, so it has to be
   * readable wet.
   */
  dead: '#D7DFE5',
  text: '#E8EEF5',
  dim: '#93A6BA',
} as const;

export type ColourName = keyof typeof PALETTE;

/**
 * The fifth surface: a flooded compartment — the water composited over the cell.
 *
 * IT IS COMPUTED, NOT TRANSCRIBED, and the renderer reads the same alpha. It was a hand-written
 * constant twice and was wrong both times, in different ways. First by one step in red (58*0.5 +
 * 27*0.5 rounds to 43, not 42) — harmless to a ratio, but a claim about what the renderer paints
 * that was only approximately true.
 *
 * THEN THE ALPHA ITSELF TURNED OUT TO BE WRONG, and only a probe of the real pixels on the deployed
 * page could see it. `--flood` clears 5.4:1 against the compartment, and every palette test agreed —
 * but the renderer painted it at 0.5 alpha, and the WATER A PLAYER ACTUALLY SEES measured **2.42:1**,
 * under the 3:1 floor for a non-text graphic. The palette proves the constants; only the canvas
 * proves the paint.
 *
 * 0.7 is not a taste call either — the satisfiable window is [0.68, 0.72] and nothing else works.
 * Raising the alpha lifts the water off the dry compartment but pushes the amber CABLE drawn on top
 * of it down: at 0.74 the water clears 3.62:1 and the cable falls to 2.96:1. Below 0.68 the water
 * itself fails. 0.7 gives water 3.39:1 vs the compartment, 3.13:1 vs a selected one, and the cable
 * 3.15:1 on the water — the only region where all three hold at once.
 */
export const FLOOD_ALPHA = 0.7;
export const FLOOD_FILL = blend(PALETTE.flood, PALETTE.cell, FLOOD_ALPHA);

/** Surfaces a meaningful mark can be drawn on. Every pair is measured. */
export const SURFACES = ['hull', 'cell', 'panel', 'sel'] as const;

// ── measurement ─────────────────────────────────────────────────────────────

export function srgbToLinear(v: number): number {
  const c = v / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

export function luminance(hex: string): number {
  const h = hex.replace('#', '');
  const r = srgbToLinear(parseInt(h.slice(0, 2), 16));
  const g = srgbToLinear(parseInt(h.slice(2, 4), 16));
  const b = srgbToLinear(parseInt(h.slice(4, 6), 16));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Composite `fg` over `bg` at `alpha`, so a translucent mark is measured as it is actually painted. */
export function blend(fg: string, bg: string, alpha: number): string {
  const p = (h: string, i: number) => parseInt(h.replace('#', '').slice(i * 2, i * 2 + 2), 16);
  const out = [0, 1, 2].map((i) => Math.round(p(fg, i) * alpha + p(bg, i) * (1 - alpha)));
  return `#${out.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Pairs that do NOT clear 3:1 and never have to, because the renderer cannot put them together.
 *
 * Each entry names the rule that makes the composite unreachable. This is deliberately a short,
 * explicit table rather than a lowered threshold: a NEW failing pair is a hard test failure, and the
 * only way to add one is to write down why it cannot happen and be checked on it.
 */
export const NEVER_COMPOSITED: { fg: ColourName; on: 'floodFill'; why: string }[] = [
  {
    fg: 'fire',
    on: 'floodFill',
    why: 'fire triangles are clipped ABOVE the waterline, and also carry a 2px stroke on --hull',
  },
  {
    fg: 'breach',
    on: 'floodFill',
    why: 'a breach is a notch in the border, on --hull, never drawn inside the fill',
  },
  {
    fg: 'dark',
    on: 'floodFill',
    why: 'the dashed tail carries its own 1px --hull halo (dark vs hull = 4.3:1)',
  },
  {
    fg: 'good',
    on: 'floodFill',
    why:
      'the only --good mark on the board is the battery charge bar, and it cannot meet the water. ' +
      'A cell DROWNS at flood 40 (charge is zeroed and the bar stops being drawn at all), and below ' +
      '40 the water occupies the bottom 40% while the bar sits 5px from the TOP — so the two are ' +
      'never on the same pixels. Everywhere else --good appears is the HUD, on --panel (9.1:1).',
  },
  {
    fg: 'flood',
    on: 'floodFill',
    why:
      'the fill IS this colour, at 50% — a flood-coloured mark inside the water is the water. ' +
      'The only marks drawn in it are the waterline, a stroke along the TOP edge of the fill against ' +
      'the dry cell above (5.4:1), and spray, which flies over --hull (6.8:1). Unreachable by ' +
      'arithmetic, not by convention: contrast(x, blend(x, cell, 0.5)) cannot reach 3:1 for any x, ' +
      'because a half composite always lands about halfway.',
  },
];
